import { createFileRoute } from "@tanstack/react-router";
import { Plus, Edit2, Trash2, Loader2, FolderHeart, Sparkles } from "lucide-react";
import { useState, useEffect } from "react";
import { toast } from "sonner";
import { API_BASE_URL } from "@/lib/api";
import lux from "@/assets/1742561569_3593189334595606656.webp";
import artisan from "@/assets/1780018027_3907396849912522036.webp";
import decor from "@/assets/1779947897_3906808566430270185.webp";
import romantic from "@/assets/1779847500_3905966368420069795.webp";

export const Route = createFileRoute("/admin/collections")({
  head: () => ({
    meta: [
      { title: "Manage Collections — Royal Lumina" },
    ],
  }),
  component: AdminCollections,
});

type FormCollection = {
  id?: string;
  name: string;
  tagline: string;
  tone: string;
  description: string;
  img: string;
};

const emptyForm: FormCollection = {
  name: "",
  tagline: "",
  tone: "",
  description: "",
  img: lux,
};

// Beautiful predefined image presets matching the store's luxury assets
const imagePresets = [
  { name: "Luxury Candle", url: lux },
  { name: "Artisan Couple", url: romantic },
  { name: "Sculpted Pine", url: decor },
  { name: "Rose Bust", url: artisan },
];

const staticCollectionsData = [
  { id: "col-1", name: "Luxury Collection", slug: "luxury-collection", tagline: "Golden hour, poured.", tone: "Amber · Oud · Saffron", description: "Luxury hand-poured candles for quiet luxury.", img: lux, _count: { products: 3 } },
  { id: "col-2", name: "Artisan Collection", slug: "artisan-collection", tagline: "Sculpted by hand, blessed by fire.", tone: "Sandalwood · Rose · Musk", description: "Artisan poured candles with detailed sculptures.", img: artisan, _count: { products: 2 } },
  { id: "col-3", name: "Decor Collection", slug: "decor-collection", tagline: "Objects that live between rituals.", tone: "Fig · Cedar · Bergamot", description: "Decorative pieces designed for home rituals.", img: decor, _count: { products: 3 } },
];

function AdminCollections() {
  const [collections, setCollections] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState<FormCollection>(emptyForm);
  const [editingId, setEditingId] = useState<string | null>(null);

  const fetchCollections = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/collections`);
      if (res.ok) {
        const data = await res.json();
        setCollections(data);
      } else {
        setCollections(staticCollectionsData);
      }
    } catch (err) {
      setCollections(staticCollectionsData);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCollections();
  }, []);

  const handleOpenCreate = () => {
    setFormData(emptyForm);
    setEditingId(null);
    setIsDialogOpen(true);
  };

  const handleOpenEdit = (c: any) => {
    setFormData({
      name: c.name,
      tagline: c.tagline || "",
      tone: c.tone || "",
      description: c.description || "",
      img: c.img || "/src/assets/1742561569_3593189334595606656.webp",
    });
    setEditingId(c.id);
    setIsDialogOpen(true);
  };

  const handleDelete = async (collection: any) => {
    const productCount = collection._count?.products || 0;
    let message = `Are you sure you want to delete the collection "${collection.name}"?`;
    if (productCount > 0) {
      message += `\n\nWarning: There are ${productCount} product(s) linked to this collection. They will remain in the store but won't belong to any collection.`;
    }

    if (!confirm(message)) return;

    try {
      const token = localStorage.getItem("supabase.auth.token") || "";
      const res = await fetch(`${API_BASE_URL}/api/collections/${collection.id}`, {
        method: "DELETE",
        headers: {
          "Authorization": `Bearer ${token}`
        }
      });

      if (res.ok) {
        toast.success("Collection deleted successfully");
        fetchCollections();
      } else {
        setCollections((prev) => prev.filter((c) => c.id !== collection.id));
        toast.success("Collection removed (Local state)");
      }
    } catch (err) {
      setCollections((prev) => prev.filter((c) => c.id !== collection.id));
      toast.success("Collection removed (Local state fallback)");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name) {
      toast.error("Please enter a collection name");
      return;
    }

    try {
      const token = localStorage.getItem("supabase.auth.token") || "";
      const url = editingId 
        ? `${API_BASE_URL}/api/collections/${editingId}`
        : `${API_BASE_URL}/api/collections`;
      
      const method = editingId ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (res.ok) {
        toast.success(editingId ? "Collection updated successfully" : "Collection added successfully");
        setIsDialogOpen(false);
        fetchCollections();
      } else {
        const errorData = await res.json();
        throw new Error(errorData.message || "API call failed");
      }
    } catch (err: any) {
      toast.error(err.message || "Failed to save collection");
      
      // Local state simulation
      if (editingId) {
        setCollections((prev) => 
          prev.map((c) => c.id === editingId ? { ...c, ...formData, slug: formData.name.toLowerCase().replace(/\s+/g, '-') } : c)
        );
        toast.success("Collection updated (Local simulation)");
      } else {
        const newCol = {
          ...formData,
          id: `col_${Date.now()}`,
          slug: formData.name.toLowerCase().replace(/\s+/g, '-'),
          _count: { products: 0 }
        };
        setCollections((prev) => [...prev, newCol]);
        toast.success("Collection added (Local simulation)");
      }
      setIsDialogOpen(false);
    }
  };

  return (
    <div className="space-y-8">
      {/* Header bar */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="font-display text-4xl tracking-tight text-primary">Collection Management</h2>
          <p className="text-sm text-muted-foreground mt-1">Manage the premium hand-poured candle collections of your store.</p>
        </div>
        <button
          onClick={handleOpenCreate}
          className="btn-gold self-start sm:self-auto"
        >
          <Plus className="h-4 w-4" />
          <span>Add Collection</span>
        </button>
      </div>

      {/* Collections Table */}
      {loading ? (
        <div className="h-64 flex items-center justify-center text-muted-foreground">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      ) : (
        <div className="bg-card border border-border rounded-[24px] overflow-hidden shadow-luxe">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm border-collapse">
              <thead>
                <tr className="bg-muted/40 border-b border-border text-[0.65rem] uppercase tracking-wider text-muted-foreground font-semibold">
                  <th className="px-6 py-4">Image</th>
                  <th className="px-6 py-4">Collection Details</th>
                  <th className="px-6 py-4">Tone Tags</th>
                  <th className="px-6 py-4">Tagline</th>
                  <th className="px-6 py-4 text-center">Linked Products</th>
                  <th className="px-6 py-4 text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/60">
                {collections.map((c) => (
                  <tr key={c.id} className="hover:bg-muted/10 transition-colors">
                    <td className="px-6 py-4 shrink-0">
                      <div className="h-12 w-16 rounded-xl bg-card border border-border overflow-hidden grid place-items-center">
                        {c.img ? (
                          <img src={c.img} alt={c.name} className="h-full w-full object-cover" />
                        ) : (
                          <FolderHeart className="h-5 w-5 text-gold/40" />
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="font-semibold text-primary">{c.name}</div>
                      <div className="font-mono text-[0.65rem] text-muted-foreground">/{c.slug}</div>
                    </td>
                    <td className="px-6 py-4">
                      {c.tone ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded bg-gold/10 text-gold text-[0.65rem] font-medium tracking-wide">
                          {c.tone}
                        </span>
                      ) : (
                        <span className="italic text-muted-foreground/30 text-xs">None</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-muted-foreground italic max-w-xs truncate" title={c.tagline}>
                      {c.tagline || <span className="italic text-muted-foreground/30">No tagline</span>}
                    </td>
                    <td className="px-6 py-4 text-center font-semibold text-primary">
                      {c._count?.products || 0}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => handleOpenEdit(c)}
                          className="p-2 rounded-lg hover:bg-muted text-muted-foreground hover:text-primary transition-colors"
                          title="Edit Collection"
                        >
                          <Edit2 className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(c)}
                          className="p-2 rounded-lg hover:bg-rose-50 text-muted-foreground hover:text-rose-600 transition-colors"
                          title="Delete Collection"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Add / Edit Dialog */}
      {isDialogOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-primary/40 backdrop-blur-sm">
          <div className="bg-card border border-border rounded-[24px] max-w-lg w-full p-8 shadow-luxe relative overflow-y-auto max-h-[90vh]">
            <h3 className="font-display text-2xl text-primary mb-6">
              {editingId ? "Edit Collection Details" : "Create New Collection"}
            </h3>

            <form onSubmit={handleSubmit} className="space-y-5">
              <label className="block">
                <span className="text-[0.65rem] tracking-[0.2em] uppercase text-muted-foreground">Collection Name</span>
                <input
                  type="text"
                  required
                  placeholder="e.g. Winter Woods Collection"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="mt-2 w-full h-11 px-4 bg-card border border-border rounded-[12px] text-sm focus:outline-none focus:border-gold"
                />
              </label>

              <div className="grid grid-cols-2 gap-4">
                <label className="block">
                  <span className="text-[0.65rem] tracking-[0.2em] uppercase text-muted-foreground">Tagline</span>
                  <input
                    type="text"
                    placeholder="e.g. Cozy up the cold."
                    value={formData.tagline}
                    onChange={(e) => setFormData({ ...formData, tagline: e.target.value })}
                    className="mt-2 w-full h-11 px-4 bg-card border border-border rounded-[12px] text-sm focus:outline-none focus:border-gold"
                  />
                </label>
                <label className="block">
                  <span className="text-[0.65rem] tracking-[0.2em] uppercase text-muted-foreground">Tone Tags</span>
                  <input
                    type="text"
                    placeholder="e.g. Cedar · Pine · Clove"
                    value={formData.tone}
                    onChange={(e) => setFormData({ ...formData, tone: e.target.value })}
                    className="mt-2 w-full h-11 px-4 bg-card border border-border rounded-[12px] text-sm focus:outline-none focus:border-gold"
                  />
                </label>
              </div>

              <label className="block">
                <span className="text-[0.65rem] tracking-[0.2em] uppercase text-muted-foreground">Description</span>
                <textarea
                  placeholder="Describe the collection rituals..."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="mt-2 w-full h-20 p-4 bg-card border border-border rounded-[12px] text-sm focus:outline-none focus:border-gold resize-none"
                />
              </label>

              <div>
                <span className="text-[0.65rem] tracking-[0.2em] uppercase text-muted-foreground block mb-2">Banner Image</span>
                
                {/* Visual Preview */}
                <div className="mb-4 h-32 w-full rounded-xl border border-border overflow-hidden relative group bg-muted flex items-center justify-center">
                  {formData.img ? (
                    <img src={formData.img} alt="Banner Preview" className="h-full w-full object-cover" />
                  ) : (
                    <Sparkles className="h-6 w-6 text-gold/40 animate-pulse" />
                  )}
                  <div className="absolute inset-0 bg-primary/20" />
                </div>

                {/* Predefined Presets */}
                <span className="text-[0.6rem] tracking-[0.1em] uppercase text-muted-foreground/70 block mb-2">Select a Preset Theme:</span>
                <div className="flex gap-2 mb-4 overflow-x-auto pb-2">
                  {imagePresets.map((preset) => {
                    const isActive = formData.img === preset.url;
                    return (
                      <button
                        type="button"
                        key={preset.name}
                        onClick={() => setFormData({ ...formData, img: preset.url })}
                        className={`h-10 px-3 rounded-lg border text-xs flex items-center gap-2 whitespace-nowrap shrink-0 transition-all ${
                          isActive 
                            ? "border-gold bg-gold/10 text-gold font-semibold" 
                            : "border-border hover:border-gold/50 text-muted-foreground"
                        }`}
                      >
                        <div className="h-4 w-6 rounded overflow-hidden border border-border">
                          <img src={preset.url} className="h-full w-full object-cover" />
                        </div>
                        {preset.name}
                      </button>
                    );
                  })}
                </div>

                <label className="block">
                  <span className="text-[0.6rem] tracking-[0.1em] uppercase text-muted-foreground/70">Or Paste Image URL</span>
                  <input
                    type="text"
                    placeholder="https://example.com/banner.jpg"
                    value={formData.img}
                    onChange={(e) => setFormData({ ...formData, img: e.target.value })}
                    className="mt-2 w-full h-11 px-4 bg-card border border-border rounded-[12px] text-sm focus:outline-none focus:border-gold"
                  />
                </label>
              </div>

              <div className="mt-8 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setIsDialogOpen(false)}
                  className="btn-outline-dark h-11 py-0 px-5 text-xs"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-gold h-11 py-0 px-6 text-xs"
                >
                  Save Collection
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
